import { useEffect, useState } from 'react'
import { BackHandler } from 'react-native'
import { useToastController } from '@my/ui'
import { uploadImage } from 'app/lib/upload-image'
import { AuthUser } from 'app/provider/auth'
import { createCustomUuid } from 'app/utils/create-custom-uuid'
import { GroupEventEditWizard } from './group-event-edit-wizard'
import { useUpdateEvent } from '../commands'
import { GroupEvent } from 'app/types'
import { SubmitEventData } from './event-creation-view'
import { concatDateAndTime } from '../helpers'
import { groupEventByIdQuery } from '../../../queries'

type GroupEventEditWizardProps = {
  open: boolean
  onOpenChange: (value) => void
  event: GroupEvent
  user: Pick<AuthUser, 'id' | 'fullName' | 'avatar' | 'token'> | null
}

export const GroupEventEditWizardContainer = (props: GroupEventEditWizardProps) => {
  const toast = useToastController()
  const [submitting, setSubmitting] = useState(false)

  const [updateEvent] = useUpdateEvent()

  useEffect(() => {
    const backAction = () => {
      props.onOpenChange(false);
      return props.open
    }

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => backHandler.remove();
  }, [])

  const handleSubmit = async (data: SubmitEventData) => {
    const { name, description, image, startDate, endDate, endTime, startTime, location } = data

    if (!props.user || !data.group?.id) {
      toast.show("User or group doesn't exist", {
        burntOptions: {
          preset: 'error',
        },
      })
      return
    }

    setSubmitting(true)

    let imageId: string | null = null
    if (image) {
      const imageName = createCustomUuid()
      const imageRes = await uploadImage({
        image: image,
        name: `post-image-${data.group?.id}-${props.user.id}--${imageName}`,
        authToken: props.user.token,
        collection: 'media',
      })
      if (imageRes.doc) {
        imageId = imageRes.doc.id
      } else {
        toast.show('Failed to upload image', {
          burntOptions: {
            preset: 'error',
          },
        })
      }
    }

    const startDateTime = concatDateAndTime(new Date(startDate), startTime)
    const endDateTime = endDate ? concatDateAndTime(new Date(endDate), endTime ?? '00:00') : null

    await updateEvent({
      variables: {
        id: props.event.eventId,
        name,
        description,
        location,
        startDate: startDateTime.toISOString(),
        ...(endDateTime && { endDate: endDateTime.toISOString() }),
        ...(imageId && { image: imageId }),
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: groupEventByIdQuery.query,
          variables: {
            id: props.event.eventId,
          },
        },
      ],
      onCompleted: async (data) => {
        if (!data?.updateEvent?.success) {
          toast.show(data?.updateEvent?.message || 'Failed to update event', {
            burntOptions: {
              preset: 'error',
            },
          })
          return
        }
        setSubmitting(false)
        props.onOpenChange(false)
        toast.show('Event updated')
      },

      onError: () => {
        toast.show('Failed to update event', {
          burntOptions: {
            preset: 'error',
          },
        })
        setSubmitting(false)
      },
    })
  }

  if (!props.user) return null

  return (
    <GroupEventEditWizard
      event={props.event}
      user={props.user}
      open={props.open}
      submitting={submitting}
      onOpenChange={props.onOpenChange}
      onSubmit={handleSubmit}
    />
  )
}
